if (!customElements.get('collection-mix-match')) {
  customElements.define(
    'collection-mix-match',
    class CollectionMixMatch extends HTMLElement {
      connectedCallback() {
        this.rows = Array.from(this.querySelectorAll('[data-collection-mix-match-row]'));
        this.onInputChange = this.handleInputChange.bind(this);
        this.onButtonClick = this.handleButtonClick.bind(this);
        this.onResetSelections = this.handleResetSelections.bind(this);

        this.rows.forEach((row) => {
          const input = row.querySelector('.collection-mix-match__qty-input');
          if (input) {
            input.addEventListener('change', this.onInputChange);
            input.addEventListener('input', this.onInputChange);
          }
        });

        this.addEventListener('click', this.onButtonClick);
        document.addEventListener('collection-mix-match:clear-selections', this.onResetSelections);
        this.updateDiscountTier();
        this.updateProgress();
        this.syncRowStates();
        this.notifySelectionChanged();
        // Re-notify after buy-buttons (DOMContentLoaded) has attached its listener.
        // Without this, the price box stays empty until the first user interaction.
        setTimeout(() => this.notifySelectionChanged(), 0);
        if (window.RioIgPrice) {
          window.RioIgPrice.whenReady().then(() => {
            this.updateDiscountTier();
            this.notifySelectionChanged();
          });
        }
      }

      disconnectedCallback() {
        this.rows.forEach((row) => {
          const input = row.querySelector('.collection-mix-match__qty-input');
          if (input) {
            input.removeEventListener('change', this.onInputChange);
            input.removeEventListener('input', this.onInputChange);
          }
        });
        this.removeEventListener('click', this.onButtonClick);
        document.removeEventListener('collection-mix-match:clear-selections', this.onResetSelections);
      }

      get isPdpMixMatch() {
        return this.dataset.pdpMixMatch === 'true';
      }

      reseedCurrentProduct() {
        if (!this.isPdpMixMatch) return;

        const currentRow = this.querySelector('[data-mix-match-current-product]');
        const input = currentRow?.querySelector('.collection-mix-match__qty-input');
        if (!input) return;

        const max = parseInt(input.getAttribute('max') || '99', 10);
        input.value = String(Math.min(max, 1));
      }

      adjustRowQuantity(row, delta) {
        const input = row.querySelector('.collection-mix-match__qty-input');
        if (!input) return;

        const min = parseInt(input.getAttribute('min') || '0', 10);
        const max = parseInt(input.getAttribute('max') || '99', 10);
        const current = Math.max(parseInt(input.value || '0', 10) || 0, 0);
        const next = Math.min(max, Math.max(min, current + delta));
        input.value = String(next);
        this.syncRowStates();
        this.updateDiscountTier();
        this.updateProgress();
        this.notifySelectionChanged();
      }

      getTotalSelectedQty() {
        return this.rows.reduce((sum, row) => {
          const input = row.querySelector('.collection-mix-match__qty-input');
          return sum + Math.max(parseInt(input?.value || '0', 10) || 0, 0);
        }, 0);
      }

      formatMoney(cents) {
        if (window.Shopify?.formatMoney) {
          return Shopify.formatMoney(cents);
        }

        const currency = document.documentElement.dataset.currency || window.Shopify?.currency?.active || 'USD';
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency,
        }).format(cents / 100);
      }

      syncRowStates() {
        this.rows.forEach((row) => {
          const input = row.querySelector('.collection-mix-match__qty-input');
          const qty = Math.max(parseInt(input?.value || '0', 10) || 0, 0);
          const addButton = row.querySelector('[data-collection-mix-match-add]');
          const qtySelector = row.querySelector('.collection-mix-match__qty');

          row.classList.toggle('is-selected', qty > 0);
          if (addButton) addButton.hidden = qty > 0;
          if (qtySelector) qtySelector.hidden = qty === 0;
        });
      }

      handleInputChange() {
        this.syncRowStates();
        this.updateDiscountTier();
        this.updateProgress();
        this.notifySelectionChanged();
      }

      handleButtonClick(event) {
        const addButton = event.target.closest('[data-collection-mix-match-add]');
        if (addButton && this.contains(addButton)) {
          const row = addButton.closest('[data-collection-mix-match-row]');
          if (!row) return;
          event.preventDefault();
          this.adjustRowQuantity(row, 1);
          return;
        }

        const button = event.target.closest('button[name="minus"], button[name="plus"]');
        if (!button || !this.contains(button)) return;

        const row = button.closest('[data-collection-mix-match-row]');
        if (!row) return;

        event.preventDefault();
        this.adjustRowQuantity(row, button.name === 'plus' ? 1 : -1);
      }

      handleResetSelections(event) {
        if (event?.detail?.formId && event.detail.formId !== this.dataset.productFormId) return;

        this.rows.forEach((row) => {
          const input = row.querySelector('.collection-mix-match__qty-input');
          if (!input) return;
          input.value = '0';
        });

        this.reseedCurrentProduct();
        this.syncRowStates();
        this.updateDiscountTier();
        this.updateProgress();
        this.notifySelectionChanged();
      }

      notifySelectionChanged() {
        document.dispatchEvent(
          new CustomEvent('collection-mix-match:selection-changed', {
            detail: {
              formId: this.dataset.productFormId,
              totalQty: this.getTotalSelectedQty(),
            },
          }),
        );
      }

      updateDiscountTier() {
        const totalQty = this.getTotalSelectedQty();
        const discountPct = totalQty >= 3 ? 25 : totalQty >= 2 ? 20 : totalQty === 1 ? 10 : 0;
        const multiplier = totalQty >= 3 ? 0.75 : totalQty >= 2 ? 0.8 : totalQty === 1 ? 0.9 : 1.0;
        const hasDiscount = discountPct > 0;

        this.rows.forEach((row) => {
          const fallback = parseInt(row.dataset.variantPriceCents || '0', 10);
          const priceCents = window.RioIgPrice
            ? window.RioIgPrice.getListPriceCents(row.dataset.variantId, fallback)
            : fallback;
          if (!priceCents) return;

          // Slash pricing only on selected rows so unselected rows stay visually quiet.
          const input = row.querySelector('.collection-mix-match__qty-input');
          const qty = Math.max(parseInt(input?.value || '0', 10) || 0, 0);
          const rowHasDiscount = hasDiscount && qty > 0;

          const compare = row.querySelector('[data-mix-match-row-compare]');
          const sale = row.querySelector('[data-mix-match-row-sale]');
          const badge = row.querySelector('[data-mix-match-row-badge]');
          const saleCents = totalQty === 1 && window.RioIgPrice
            ? window.RioIgPrice.qtyOneSaleCents(priceCents)
            : Math.round(priceCents * multiplier);

          if (compare) {
            compare.textContent = this.formatMoney(priceCents);
            compare.style.display = rowHasDiscount ? '' : 'none';
          }
          if (sale) sale.textContent = this.formatMoney(rowHasDiscount ? saleCents : priceCents);
          if (badge) {
            badge.textContent = rowHasDiscount
              ? (discountPct === 10 ? '10% off automatically applied' : discountPct + '% off')
              : '';
            badge.style.display = rowHasDiscount ? '' : 'none';
          }
        });
      }

      updateProgress() {
        const totalQty = this.getTotalSelectedQty();
        const progress = this.querySelector('[data-collection-mix-match-progress]');
        if (!progress) return;

        const fill = progress.querySelector('[data-mix-match-progress-fill]');
        const track = progress.querySelector('.collection-mix-match__progress-track');
        const milestoneOne = progress.querySelector('[data-mix-match-milestone="one"]');
        const milestoneMulti = progress.querySelector('[data-mix-match-milestone="multi"]');

        let progressPct = 0;
        if (totalQty >= 3) progressPct = 100;
        else if (totalQty === 2) progressPct = 66;
        else if (totalQty === 1) progressPct = 33;

        if (fill) {
          fill.style.width = progressPct + '%';
          fill.style.setProperty('--mix-match-progress', progressPct + '%');
        }

        this.style.setProperty('--mix-match-progress', progressPct + '%');
        track?.style.setProperty('--mix-match-progress', progressPct + '%');

        milestoneOne?.classList.toggle('is-unlocked', totalQty >= 2);
        milestoneMulti?.classList.toggle('is-unlocked', totalQty >= 3);
      }
    },
  );
}
